"use strict";
// Copyright 2019 Centrality Investments Limited
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("@cennznet/types");
const polkadot_1 = require("@cennznet/types/polkadot");
const util_1 = require("@cennznet/util");
const bn_js_1 = __importDefault(require("bn.js"));
const constants_1 = require("../constants");
/**
 * Generate the key of the balance storage
 * @param prefixString
 * @param assetId
 * @param address
 */
function generateExchangeAddress(coreAssetId, assetId) {
    // key1 encoded
    const prefixU8a = util_1.stringToU8a('cennz-x-spot:');
    const key1U8a = new polkadot_1.u64(coreAssetId).toU8a(true);
    const key2U8a = new polkadot_1.u64(assetId).toU8a(true);
    const keyEncoded = util_1.blake2AsU8a(util_1.u8aConcat(prefixU8a, key1U8a, key2U8a));
    return new polkadot_1.Address(keyEncoded).toString();
}
exports.generateExchangeAddress = generateExchangeAddress;
/**
 * create exchange key
 * @param coreAssetId
 * @param assetId
 */
function getExchangeKey(coreAssetId, assetId) {
    return new polkadot_1.Tuple([types_1.AssetId, types_1.AssetId], [coreAssetId, assetId]);
}
exports.getExchangeKey = getExchangeKey;
function getOutputPrice(outputAmount, inputReserve, outputReserve, feeRate) {
    if (inputReserve.isZero() || outputReserve.isZero()) {
        return new bn_js_1.default(0);
    }
    const output = inputReserve
        .mul(outputAmount)
        .div(outputReserve.sub(outputAmount))
        .addn(1);
    return feeRate
        .mul(output)
        .divn(constants_1.PERMILL_BASE)
        .add(output);
}
exports.getOutputPrice = getOutputPrice;
function generateStorageDoubleMapKey(prefixString, key1, key2) {
    // key1 encoded
    const prefixU8a = util_1.stringToU8a(prefixString);
    const key1U8a = key1.toU8a(true);
    const key1Encoded = new Uint8Array(prefixU8a.length + key1U8a.length);
    key1Encoded.set(prefixU8a);
    key1Encoded.set(key1U8a, prefixU8a.length);
    // key2 encoded
    const length = 2;
    const bitLength = 128;
    const key2Encoded = util_1.xxhashAsHex(key2.toU8a(true), bitLength).substr(length);
    // tslint:disable-next-line:no-magic-numbers
    return util_1.blake2AsHex(key1Encoded, 256) + key2Encoded;
}
exports.generateStorageDoubleMapKey = generateStorageDoubleMapKey;
function getInputPrice(inputAmount, inputReserve, outputReserve, feeRate) {
    if (inputReserve.isZero() || outputReserve.isZero()) {
        return new bn_js_1.default(0);
    }
    const divRate = feeRate.addn(constants_1.PERMILL_BASE);
    const inputAmountLessFeeScaled = inputAmount
        .muln(constants_1.PERMILL_BASE)
        .muln(constants_1.PERMILL_BASE)
        .div(divRate);
    const numerator = inputAmountLessFeeScaled.mul(outputReserve);
    const denominator = inputAmount
        .muln(constants_1.PERMILL_BASE)
        .div(divRate)
        .add(inputReserve);
    return numerator.div(denominator).divn(constants_1.PERMILL_BASE);
}
exports.getInputPrice = getInputPrice;
//# sourceMappingURL=utils.js.map